import type { MmaCsvRow } from "./parseMmaCsv"

// Per-flight CSV telemetry registry. Both the InfraredImage (NIR/thermal) and
// Orthomosaic (RGB) uploaders pull from this — a flight's telemetry is the same
// regardless of which band is being uploaded.
const flightCsvLookup: Record<string, Map<string, MmaCsvRow>> = {}

export const getFlightCsv = (flightId: string): Map<string, MmaCsvRow> | undefined =>
    flightCsvLookup[flightId]

export const mergeFlightCsv = (flightId: string, rows: Map<string, MmaCsvRow>) => {
    const existing = flightCsvLookup[flightId]
    if (existing) {
        rows.forEach((v, k) => existing.set(k, v))
    } else {
        flightCsvLookup[flightId] = rows
    }
}

export const clearFlightCsv = (flightId: string) => {
    delete flightCsvLookup[flightId]
}

// Side-channel for CSV-only drops in the "add more files" dropbox.
// routeCsvFiles sets this; stageFiles drains it immediately after (same microtask).
let pendingCsvDropFiles: File[] = []
export const setPendingCsvDrop = (files: File[]) => {
    pendingCsvDropFiles = files
}
export const drainPendingCsvDrop = (): File[] => pendingCsvDropFiles.splice(0)

const isCsvFile = (f: File) => f.name.toLowerCase().endsWith(".csv")

/**
 * Pulls MMA telemetry CSVs out of a drop and parks them in the side-channel so
 * stageFiles can dispatch them with the correct flightId; returns the remaining
 * (image) files. A no-op when no CSVs are present.
 */
export const routeCsvFiles = (files: File[]): File[] => {
    const csvFiles = files.filter(isCsvFile)
    if (csvFiles.length === 0) return files
    setPendingCsvDrop(csvFiles)
    return files.filter((f) => !isCsvFile(f))
}

export type CsvConversionKind = "png" | "owi"

/**
 * Raster sources that carry no embedded geo metadata and must be paired with an
 * MMA telemetry CSV before upload. Returns the conversion kind, or null for
 * formats that are uploaded as-is (jpg, tiff, irb, ...).
 */
export const csvConversionKind = (fileName: string): CsvConversionKind | null => {
    const name = fileName.toLowerCase()
    if (name.endsWith(".png")) return "png"
    if (name.endsWith(".owi")) return "owi"
    return null
}

export const requiresCsvTelemetry = (fileName: string): boolean =>
    csvConversionKind(fileName) !== null
